import React, { useState } from 'react';
import Loader from '../../components/loader';
import { useDeliveryOrderBatches, useStartDepots, useVehicleTypes } from './apiHooks';
import NumberInput from './components/NumberInput';
import { FormattedMessage, injectIntl } from 'react-intl';
import AdminRouteOptimisationUserTable from './adminRoutePlanDeliveryOrderBatchTable';
import AdminRoutePlanStartDepot from './adminRoutePlanStartDepot';

const AdminRoutePlanCreate = ({ timeRange, onCreateRoutePlan, routeLoading, intl }) => {
  const { vehicleTypes } = useVehicleTypes();
  const [startDepot, setStartDepot] = useState(null);
  const [vehicles, setVehicles] = useState(vehicleTypes.map((type) => ({ ...type, count: 0 })));
  const { deliveryOrderBatches, isLoading: loadingDeliveryOrderBatches } = useDeliveryOrderBatches({
    deliveryDayTimeRangeId: timeRange.id,
  });
  const allAddressesHaveCoordinates = deliveryOrderBatches?.every((dob) => !!dob?.address?.coordinates);
  const someVehiclesPresent = vehicles.reduce((sum, vehicle) => sum + vehicle.count, 0) > 0;

  function handleRouteCreation() {
    if (!allAddressesHaveCoordinates || !timeRange || !someVehiclesPresent) {
      return;
    }
    return onCreateRoutePlan({
      routePlan: {
        deliveryDayTimeRangeId: timeRange.id,
        vehicles: vehicles
          .filter((vehicle) => !!vehicle.count)
          .map((vehicle) => ({
            type: vehicle.id,
            count: vehicle.count,
            capacity: vehicle.capacity,
          })),
        startDepotId: startDepot.id,
      },
    });
  }

  if (loadingDeliveryOrderBatches) {
    return <Loader show={true} />;
  }

  if (!deliveryOrderBatches || !deliveryOrderBatches.length) {
    return (
      <div className="alert alert-warning">
        <FormattedMessage id={'admin.route.planning.create.order.batches.not.created'} />
      </div>
    );
  }

  return (
    <>
      <div className="m-b-6">
        <AdminRoutePlanStartDepot setStartDepot={setStartDepot} startDepot={startDepot} />
      </div>
      <div className="m-b-3">
        <h3>
          <FormattedMessage id={'admin.route.planning.create.vehicles.header'} />
        </h3>
        <div className="row">
          {vehicles.map((vehicle) => (
            <div className="col-12 col-sm-6 col-md-4 m-b-3" key={vehicle.id}>
              <div className="m-b-0">
                <label htmlFor={vehicle.id}>
                  <strong>{vehicle.name}</strong>
                </label>

                <NumberInput
                  id={vehicle.id}
                  value={vehicle.count}
                  onChange={(count) =>
                    setVehicles((previous) =>
                      previous.map((current) => (current.id === vehicle.id ? { ...current, count } : current))
                    )
                  }
                />
              </div>
              <div className="">
                <label htmlFor={`capacity-${vehicle.id}`}>
                  <FormattedMessage id={'admin.route.planning.create.vehicles.field.capacity'} />
                </label>

                <NumberInput
                  id={vehicle.id}
                  value={vehicle.capacity}
                  onChange={(capacity) =>
                    setVehicles((previous) =>
                      previous.map((current) => (current.id === vehicle.id ? { ...current, capacity } : current))
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <h3>
        <FormattedMessage id={'admin.route.planning.create.customers.header'} />
      </h3>
      <AdminRouteOptimisationUserTable deliveryOrderBatches={deliveryOrderBatches} />
      <div className="m-b-3">
        <FormattedMessage
          id={'admin.route.planning.create.customers.count'}
          values={{ count: deliveryOrderBatches.length }}
        />
      </div>

      <button
        disabled={!allAddressesHaveCoordinates || !someVehiclesPresent || routeLoading || !startDepot}
        onClick={handleRouteCreation}
        className={`btn btn-primary m-t-2 ${routeLoading ? 'btn-loading' : ''}`}
      >
        {someVehiclesPresent && allAddressesHaveCoordinates && startDepot
          ? intl.formatMessage({ id: 'admin.route.planning.create.cta.generate.routes' })
          : ''}
        {!allAddressesHaveCoordinates
          ? intl.formatMessage({ id: 'admin.route.planning.create.cta.add.coordinates' })
          : ''}
        {!someVehiclesPresent && allAddressesHaveCoordinates
          ? intl.formatMessage({ id: 'admin.route.planning.create.cta.add.vehicles' })
          : ''}
        {!startDepot ? intl.formatMessage({ id: 'admin.route.planning.create.cta.add.start.depot' }) : ''}
      </button>
    </>
  );
};

export default injectIntl(AdminRoutePlanCreate);
